import { useEffect, useRef, useState } from 'react';
import { streamPlannerLogs } from '../api/llmLogsApi';
import type { LlmLogDto } from '../types/engineering';
import { emitLlmEvent } from '../hooks/useLlmEvents';

const TERMINAL_TYPES = new Set(['DONE', 'ERROR']);

interface LlmLogPanelProps {
  executionId: string;
  agent?: string;
  onDone?: () => void;
}

export const LlmLogPanel = ({ executionId, agent = 'PLANNER', onDone }: LlmLogPanelProps) => {
  const [logs, setLogs] = useState<LlmLogDto[]>([]);
  const [dots, setDots] = useState('.');
  const bottomRef = useRef<HTMLDivElement>(null);
  const seenIds = useRef<Set<string>>(new Set());
  const isDone = useRef(false);
  const hasCalledOnDone = useRef(false);
  const closeRef = useRef<(() => void) | null>(null);
  const onDoneRef = useRef(onDone);

  // Keep onDoneRef in sync with the latest onDone callback
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  // Animate the dots while no logs have arrived
  useEffect(() => {
    if (logs.length > 0) return;
    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '.' : d + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, [logs.length]);

  // SSE connection with auto-reconnect
  useEffect(() => {
    isDone.current = false;
    hasCalledOnDone.current = false;
    seenIds.current.clear();
    setLogs([]); // Clear previous logs when component remounts

    closeRef.current = streamPlannerLogs(
      executionId,
      (log) => {
        if (seenIds.current.has(log.id)) return;
        seenIds.current.add(log.id);
        setLogs((prev) => [...prev, log]);
        // Only trigger onDone for NEW DONE events (not from history)
        // Check if this DONE happened recently (within last 10 seconds)
        const logTime = new Date(log.timestamp).getTime();
        const now = Date.now();
        const isRecent = (now - logTime) < 10000; // 10 seconds
        
        if (TERMINAL_TYPES.has(log.typeMessage) && isRecent) {
          isDone.current = true;
          // Emit event for cross-component updates
          emitLlmEvent('llm:done', { executionId, agent: log.agent, typeMessage: log.typeMessage });
          // Refresh screen data once — immediately when DONE log arrives, not waiting for stream-end
          if (!hasCalledOnDone.current) {
            hasCalledOnDone.current = true;
            console.log(`[LlmLogPanel] Calling onDone for RECENT ${log.typeMessage} event`);
            onDoneRef.current?.();
          }
        } else if (TERMINAL_TYPES.has(log.typeMessage) && !isRecent) {
          console.log(`[LlmLogPanel] Ignoring OLD ${log.typeMessage} event from ${log.timestamp}`);
        }
      },
      () => {
        // Backend sent stream-end — fallback: call onDone only if DONE log was never received
        isDone.current = true;
        emitLlmEvent('llm:done', { executionId, agent });
        if (!hasCalledOnDone.current) {
          hasCalledOnDone.current = true;
          onDoneRef.current?.();
        }
      },
      () => {
        // Genuine network error — connection lost, nothing to do
        isDone.current = true;
      },
      agent,
    );

    return () => {
      isDone.current = true;
      hasCalledOnDone.current = false; // Reset on unmount
      closeRef.current?.();
    };
  }, [executionId, agent]);

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const formatTimestamp = (ts: string) => {
    try {
      return new Date(ts).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const typeMessageColor = (typeMessage: string): string => {
    switch (typeMessage) {
      case 'START': return '#4ade80';        // green
      case 'DONE': return '#60a5fa';         // blue
      case 'ERROR': return '#f87171';        // red
      case 'THINKING': return '#facc15';     // yellow
      case 'TOOL_CALL': return '#a78bfa';    // purple
      case 'TOOL_RESULT': return '#c084fc';  // purple light
      case 'PLAN_CREATED': return '#34d399'; // teal
      case 'CODE_GENERATED': return '#38bdf8'; // sky
      case 'TEST_STARTED': return '#fb923c'; // orange
      case 'TEST_FAILED': return '#f87171';  // red
      case 'PR_CREATED': return '#4ade80';   // green
      case 'STATUS_CHANGED': return '#94a3b8'; // slate
      default: return '#e2e8f0';
    }
  };

  return (
    <div
      style={{
        marginTop: '0.75rem',
        backgroundColor: '#0f172a',
        borderRadius: '6px',
        border: '1px solid #1e293b',
        padding: '1rem',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        lineHeight: '1.6',
        color: '#e2e8f0',
        maxHeight: '400px',
        overflowY: 'auto',
      }}
    >
      {logs.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
          <span>Waiting container start</span>
          <span style={{ letterSpacing: '0.15em', fontSize: '1rem' }}>{dots}</span>
        </div>
      ) : (
        logs.map((log) => (
          <div key={log.id} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.25rem' }}>
            <span style={{ color: '#475569', flexShrink: 0 }}>{formatTimestamp(log.timestamp)}</span>
            <span
              style={{
                color: typeMessageColor(log.typeMessage),
                fontWeight: 600,
                flexShrink: 0,
                minWidth: '11ch',
              }}
            >
              {log.typeMessage}
            </span>
            <span style={{ color: '#e2e8f0', wordBreak: 'break-word' }}>{log.message}</span>
          </div>
        ))
      )}
      <div ref={bottomRef} />
    </div>
  );
};
